import { useEffect, useState } from 'react'
import { Mail, Send, Eye, Save } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { useNotify } from '@/components/shared/useNotify'
import {
  useEmailEvents,
  useEmailSettings,
  useUpdateEmailSettings,
  type EmailEvent,
  type EmailEventsResponse,
  type EmailSettings,
  type UpdateEmailSettingsInput,
  type AlertSeverity,
} from '@/hooks/useEmailSettings'

// ─── Static lookups ───────────────────────────────────────────────────────────

// KAN-842 — the stored Resend event set (migration 0118 CHECK). Opens and
// clicks are not tracked (KAN-692 design §8.5), so there is no badge for them.
const EVENT_BADGE: Record<string, { label: string; className: string }> = {
  'email.delivered': { label: 'Delivered', className: 'bg-blue-500/15 text-blue-600 border-blue-200' },
  'email.delivery_delayed': { label: 'Delayed', className: 'bg-muted text-muted-foreground border' },
  'email.bounced': { label: 'Bounced', className: 'bg-red-500/15 text-red-600 border-red-200' },
  'email.complained': { label: 'Complained', className: 'bg-amber-500/15 text-amber-600 border-amber-200' },
  'email.suppressed': { label: 'Suppressed', className: 'bg-amber-500/15 text-amber-600 border-amber-200' },
  'email.failed': { label: 'Failed', className: 'bg-red-500/15 text-red-600 border-red-200' },
}

const SEND_KIND_LABEL: Record<string, string> = {
  regulatory_digest: 'Regulatory digest',
  briefing: 'Briefing',
  alert_cve: 'CVE alert',
  alert_ransomware_burst: 'Ransomware alert',
  alert_supply_chain_critical: 'Supply-chain alert',
}

const CONTENT_TYPES = [
  { id: 'articles', label: 'Articles & Reports' },
  { id: 'regulatory', label: 'Regulatory Updates' },
  { id: 'vulnerabilities', label: 'Vulnerabilities' },
  { id: 'vendor_intel', label: 'Vendor Intelligence' },
]

const ALERT_SEVERITIES: AlertSeverity[] = ['low', 'medium', 'high', 'critical']

// ─── Briefing settings card ───────────────────────────────────────────────────

interface BriefingSettingsProps {
  settings: EmailSettings
}

function BriefingSettings({ settings }: BriefingSettingsProps) {
  const { notify } = useNotify()
  const update = useUpdateEmailSettings()

  // Local draft so Save is an explicit action (matches the original UX).
  const [sendTime, setSendTime] = useState(settings.briefingSendTime)
  const [maxItems, setMaxItems] = useState(String(settings.briefingMaxItems))
  const [contentTypes, setContentTypes] = useState<string[]>(settings.briefingContentTypes)
  const [senderName, setSenderName] = useState(settings.senderName)
  const [senderAddress, setSenderAddress] = useState(settings.senderAddress)
  const [alertEnabled, setAlertEnabled] = useState(settings.alertEnabled)
  const [alertMinSeverity, setAlertMinSeverity] = useState<AlertSeverity>(
    settings.alertMinSeverity,
  )
  const [testEmail, setTestEmail] = useState('')

  // Re-seed local state when the query data changes (e.g. after PATCH refresh).
  useEffect(() => {
    setSendTime(settings.briefingSendTime)
    setMaxItems(String(settings.briefingMaxItems))
    setContentTypes(settings.briefingContentTypes)
    setSenderName(settings.senderName)
    setSenderAddress(settings.senderAddress)
    setAlertEnabled(settings.alertEnabled)
    setAlertMinSeverity(settings.alertMinSeverity)
  }, [settings])

  function toggleType(id: string) {
    setContentTypes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    )
  }

  function handleSave() {
    const parsedMax = Number(maxItems)
    if (!Number.isFinite(parsedMax) || parsedMax < 1 || parsedMax > 50) {
      notify({
        title: 'Invalid max items',
        description: 'Must be a number between 1 and 50.',
        variant: 'error',
      })
      return
    }

    const input: UpdateEmailSettingsInput = {
      senderName,
      senderAddress,
      briefingSendTime: sendTime,
      briefingMaxItems: parsedMax,
      briefingContentTypes: contentTypes,
      alertEnabled,
      alertMinSeverity,
    }

    update.mutate(input, {
      onSuccess: () => {
        notify({ title: 'Email settings saved', variant: 'success' })
      },
      onError: (err) => {
        notify({
          title: 'Could not save settings',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'error',
        })
      },
    })
  }

  function handlePreview() {
    // TODO(email): wire to a future /admin/email/preview endpoint that renders
    // a sample briefing against the current draft settings.
    notify({
      title: 'Preview not implemented yet',
      description: 'Will render a sample briefing once the API endpoint lands.',
    })
  }

  function handleTestSend() {
    // TODO(email): wire to a future /admin/email/test-send endpoint that
    // enqueues a test briefing job for the given recipient.
    notify({
      title: 'Test send not implemented yet',
      description: `Would send test briefing to ${testEmail}.`,
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Briefing Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Sender identity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="sender-name">Sender Name</Label>
            <Input
              id="sender-name"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender-address">Sender Address</Label>
            <Input
              id="sender-address"
              type="email"
              value={senderAddress}
              onChange={(e) => setSenderAddress(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Send time + max items */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="send-time">Send Time (UTC)</Label>
            <Input
              id="send-time"
              type="time"
              value={sendTime}
              onChange={(e) => setSendTime(e.target.value)}
              className="w-36"
            />
            <p className="text-xs text-muted-foreground">Daily briefing dispatch time</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max-items">Max Items per Briefing</Label>
            <Input
              id="max-items"
              type="number"
              min="1"
              max="50"
              value={maxItems}
              onChange={(e) => setMaxItems(e.target.value)}
              className="w-24"
            />
            <p className="text-xs text-muted-foreground">Top-scored items included</p>
          </div>
        </div>

        {/* Content types */}
        <div className="space-y-2">
          <Label>Content Types to Include</Label>
          <div className="flex flex-wrap gap-3 pt-1">
            {CONTENT_TYPES.map(({ id, label }) => (
              <label
                key={id}
                className="flex items-center gap-2 cursor-pointer select-none text-sm"
              >
                <input
                  type="checkbox"
                  checked={contentTypes.includes(id)}
                  onChange={() => toggleType(id)}
                  className="accent-primary h-4 w-4"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Alert thresholds */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={alertEnabled}
                onChange={(e) => setAlertEnabled(e.target.checked)}
                className="accent-primary h-4 w-4"
              />
              Enable critical alerts
            </Label>
            <p className="text-xs text-muted-foreground">Out-of-band emails for high-impact events</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alert-severity">Minimum Severity</Label>
            <select
              id="alert-severity"
              value={alertMinSeverity}
              onChange={(e) => setAlertMinSeverity(e.target.value as AlertSeverity)}
              disabled={!alertEnabled}
              className="h-9 rounded-md border bg-background px-2 text-sm w-36 disabled:opacity-50"
            >
              {ALERT_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s[0].toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview + test send */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={handlePreview}>
            <Eye className="h-4 w-4 mr-2" />
            Preview Briefing
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="test-email">Send Test To</Label>
            <Input
              id="test-email"
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-64"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestSend}
            disabled={!testEmail}
          >
            <Send className="h-4 w-4 mr-2" />
            Send Test
          </Button>
        </div>

        {/* Save */}
        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            {settings.updatedBy
              ? `Last updated by ${settings.updatedBy} on ${new Date(settings.updatedAt).toLocaleString()}`
              : `Last updated ${new Date(settings.updatedAt).toLocaleString()}`}
          </p>
          <Button size="sm" onClick={handleSave} disabled={update.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {update.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Delivery metrics card ────────────────────────────────────────────────────

function DeliveryMetrics() {
  const { data, isLoading, isError, refetch } = useEmailEvents(50)

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email Delivery Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState
            title="Could not load delivery events"
            message="The webhook feed may be down."
            onRetry={() => refetch()}
          />
        </CardContent>
      </Card>
    )
  }

  const live = data?.status === 'live'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Email Delivery Metrics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : !data || !live ? (
          <EmptyFeedNotice data={data} />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {computeStats(data).map(({ label, value, sub }) => (
              <div key={label} className="rounded-lg border bg-card p-3 text-center">
                <p className="text-2xl font-bold tabular-nums">{value}</p>
                <p className="text-sm font-medium mt-0.5">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        )}

        {live && data ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Send</th>
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((ev) => {
                  const badge =
                    EVENT_BADGE[ev.eventType] ?? {
                      label: ev.eventType,
                      className: 'bg-muted text-muted-foreground border',
                    }
                  return (
                    <tr key={ev.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                        {formatDate(ev.occurredAt)}
                      </td>
                      <td className="px-4 py-2.5">{SEND_KIND_LABEL[ev.sendKind] ?? ev.sendKind}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{ev.detailClass ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {data && data.legacyEvents.length > 0 ? <LegacyEvents events={data.legacyEvents} /> : null}

        <p className="text-xs text-muted-foreground">
          Resend webhook events matched to sends recorded by the send ledger. Opens and clicks are not tracked.
        </p>
      </CardContent>
    </Card>
  )
}

function EmptyFeedNotice({ data }: { data: EmailEventsResponse | undefined }) {
  if (data?.status === 'awaiting_events') {
    return (
      <p className="rounded-md border px-4 py-6 text-center text-sm text-muted-foreground">
        {data.ledger.attempts} sends recorded, no provider events stored yet. Events appear once the Resend
        webhook endpoint for IntelHub is set up and Resend reports on those sends.
      </p>
    )
  }
  return (
    <p className="rounded-md border px-4 py-6 text-center text-sm text-muted-foreground">
      No events: the send ledger has recorded no sends, so no delivery event can be attributed. The ledger
      is controlled by <code>EMAIL_SEND_LEDGER_MODE</code> on the email worker.
    </p>
  )
}

function LegacyEvents({ events }: { events: EmailEvent[] }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Legacy events (before KAN-692)</p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id} className="border-b last:border-0">
                <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{formatDate(ev.occurredAt)}</td>
                <td className="px-4 py-2">{ev.eventType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function computeStats(data: EmailEventsResponse) {
  const n = (type: string) => data.counts[type] ?? 0
  const sub = `last ${data.countsWindowDays} days`
  return [
    { label: 'Delivered', value: String(n('email.delivered')), sub },
    { label: 'Bounced', value: String(n('email.bounced')), sub },
    { label: 'Complained', value: String(n('email.complained')), sub },
    {
      label: 'Suppressed / failed',
      value: String(n('email.suppressed') + n('email.failed')),
      sub,
    },
  ]
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ')
  } catch {
    return iso
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EmailConfigPage() {
  const { data, isLoading, isError, refetch } = useEmailSettings()

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Mail className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Configuration</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Configure briefing delivery and monitor email metrics
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-1/2" />
          </CardContent>
        </Card>
      ) : isError || !data ? (
        <Card>
          <CardContent className="pt-6">
            <ErrorState
              title="Could not load email settings"
              message="The admin API did not respond or returned an error."
              onRetry={() => refetch()}
            />
          </CardContent>
        </Card>
      ) : (
        <BriefingSettings settings={data.settings} />
      )}

      <DeliveryMetrics />
    </div>
  )
}
